import { useCallback, useEffect } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { v4 as uuidV4 } from 'uuid';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';
import { getFunnelProgress, initializeFunnel } from 'utils/funnel-validation';
import useUser from 'hooks/use-user';

const ACTIVE_FUNNEL_EXPERIMENTS_FLAG = 'active-funnel-experiments';
const LOCAL_EXPERIMENT_STORE_KEY_PREFIX = 'ld-experiments-';

export const getActiveExperimentsList = (userKey: string | null): string[] => {
  if (!userKey) {
    return [];
  }
  const experimentStore = getPersistedValue(`${LOCAL_EXPERIMENT_STORE_KEY_PREFIX}${userKey}`, {});
  return Object.keys(experimentStore);
};

export type ExperimentStore = Record<string, number>;
type IsUserEnrolledInExperimentReturn = { isEnrolled: boolean; experimentVariant?: string };

export type ExperimentConfig = {
  experimentFlagName: string;
  funnelSteps?: string[];
  gatingFlagName?: string;
  resetFunnelOnEnrollment?: boolean;
};

export type UseLaunchDarklyExperimentReturn = {
  isExperimentActive: () => boolean;
  isUserEnrolledInExperiment: () => IsUserEnrolledInExperimentReturn;
  enrollInExperiment: (callback?: (experimentVariant?: string) => void) => void;
  isEligibleForExperiment: () => boolean;
  hasFunnelProgress: () => boolean;
};

// Documentation for how to create a launch darkly funnel experiment: https://dutchie.atlassian.net/wiki/spaces/EN/pages/21443936258/LaunchDarkly+Funnel+Experiments+Guide
export default function useLaunchDarklyExperiment(config: ExperimentConfig): UseLaunchDarklyExperimentReturn {
  const client = useLDClient();
  const User = useUser();

  const { experimentFlagName, gatingFlagName, funnelSteps, resetFunnelOnEnrollment = true } = config;

  const getUserKey = useCallback(() => getPersistedValue(`launch-darkly-client-id`, null), []);

  const isExperimentActive = useCallback((): boolean => {
    if (!client) {
      return false;
    }
    const activeExperiments = client.variation(ACTIVE_FUNNEL_EXPERIMENTS_FLAG, {});
    return Boolean(activeExperiments[experimentFlagName]);
  }, [client, experimentFlagName]);

  const isUserEnrolledInExperiment = useCallback((): IsUserEnrolledInExperimentReturn => {
    const userKey = getUserKey();
    if (!userKey || !client) {
      return { isEnrolled: false };
    }
    const currentStore = getPersistedValue(`${LOCAL_EXPERIMENT_STORE_KEY_PREFIX}${String(userKey)}`, {});
    const experimentInCache = Boolean(currentStore[experimentFlagName]);
    const detail = client.variationDetail(experimentFlagName);
    const userEnrolledInExperiment = detail.reason?.inExperiment === true;
    return { isEnrolled: experimentInCache && userEnrolledInExperiment, experimentVariant: detail.value };
  }, [client, experimentFlagName, getUserKey]);

  const isEligibleForExperiment = useCallback((): boolean => {
    if (!client || !isExperimentActive()) {
      return false;
    }

    // If no gating flag provided, eligible if experiment is active
    if (!gatingFlagName) {
      return true;
    }

    return client.variation(gatingFlagName, false);
  }, [client, gatingFlagName, isExperimentActive]);

  const hasFunnelProgress = useCallback((): boolean => {
    const userKey = getUserKey();
    return !!getFunnelProgress(experimentFlagName, String(userKey));
  }, [experimentFlagName, getUserKey]);

  const shouldInitializeFunnel = useCallback((): boolean => resetFunnelOnEnrollment || !hasFunnelProgress(), [
    resetFunnelOnEnrollment,
    hasFunnelProgress,
  ]);

  const handleExistingEnrollment = useCallback(
    (experimentVariant?: string, onEnrolled?: (experimentVariant?: string) => void): void => {
      if (funnelSteps && shouldInitializeFunnel()) {
        const userKey = getUserKey();
        initializeFunnel(experimentFlagName, String(userKey), funnelSteps);
      }
      onEnrolled?.(experimentVariant);
    },
    [experimentFlagName, funnelSteps, shouldInitializeFunnel, getUserKey]
  );

  const updateLocalStorageEnrollment = useCallback(
    (action: 'add' | 'remove'): void => {
      let userKey = getUserKey();

      // If no userKey exists, generate one and persist it immediately
      if (!userKey) {
        userKey = uuidV4();
        setPersistedValue(`launch-darkly-client-id`, userKey);
      }

      const storageKey = `${LOCAL_EXPERIMENT_STORE_KEY_PREFIX}${String(userKey)}`;
      const currentStore = getPersistedValue(storageKey, {});

      const newStore =
        action === 'add'
          ? {
              ...currentStore,
              [experimentFlagName]: Date.now(),
            }
          : Object.fromEntries(Object.entries(currentStore).filter(([key]) => key !== experimentFlagName));

      setPersistedValue(storageKey, newStore);
    },
    [experimentFlagName, getUserKey]
  );

  const handleNewEnrollment = useCallback(
    (onEnrolled?: (experimentVariant?: string) => void): void => {
      // Step 1: Optimistically add to localStorage so LD can see it in activeExperiments context
      updateLocalStorageEnrollment('add');

      // Step 2: Set callback to verify after LD processes the context
      User.setPendingExperimentCallback(() => {
        const detail = client?.variationDetail(experimentFlagName);
        const actuallyInExperiment = detail?.reason?.inExperiment === true;

        if (actuallyInExperiment) {
          // LD enrolled them - initialize funnel tracking
          if (funnelSteps && shouldInitializeFunnel()) {
            const userKey = getUserKey();
            initializeFunnel(experimentFlagName, String(userKey), funnelSteps);
          }
        } else {
          // LD rejected them - clean up localStorage to prevent pollution
          updateLocalStorageEnrollment('remove');
        }
        onEnrolled?.(detail?.value);
      });

      // Trigger client.identify() with updated context
      User.incrementExperimentEnrollmentVersion();
    },
    [updateLocalStorageEnrollment, User, client, experimentFlagName, funnelSteps, shouldInitializeFunnel, getUserKey]
  );

  const enrollInExperiment = useCallback(
    (onEnrolled?: (experimentVariant?: string) => void): void => {
      const { isEnrolled, experimentVariant } = isUserEnrolledInExperiment();

      if (isEnrolled) {
        handleExistingEnrollment(experimentVariant, onEnrolled);
      } else {
        handleNewEnrollment(onEnrolled);
      }
    },
    [isUserEnrolledInExperiment, handleExistingEnrollment, handleNewEnrollment]
  );

  // Cleanup experiments in users local cache that are no longer active
  const cleanupStaleExposures = useCallback((): void => {
    const userKey = getUserKey();
    if (!userKey || !client) {
      return;
    }

    const activeExperiments = client.variation(ACTIVE_FUNNEL_EXPERIMENTS_FLAG, {});
    const currentExperiments = getPersistedValue(`${LOCAL_EXPERIMENT_STORE_KEY_PREFIX}${String(userKey)}`, {});

    const validExperiments = Object.fromEntries(
      Object.entries(currentExperiments).filter(([name]) => activeExperiments[name])
    );

    if (Object.keys(validExperiments).length < Object.keys(currentExperiments).length) {
      setPersistedValue(`${LOCAL_EXPERIMENT_STORE_KEY_PREFIX}${String(userKey)}`, validExperiments);
      User.incrementExperimentEnrollmentVersion();
    }
  }, [client, User, getUserKey]);

  useEffect(cleanupStaleExposures, [cleanupStaleExposures]);

  return {
    isEligibleForExperiment,
    isExperimentActive,
    isUserEnrolledInExperiment,
    enrollInExperiment,
    hasFunnelProgress,
  };
}
